import { lazy, Suspense } from 'react';
import { NuqsAdapter } from 'nuqs/adapters/react-router/v7';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import { ProductAnalyticsRouteTracker } from './components/analytics/ProductAnalyticsRouteTracker';

const FlowListPage = lazy(() => import('@/pages/flows/FlowList.Page'));
const FlowEditorPage = lazy(() => import('@/pages/flows/FlowEditor.Page'));
const FlowTemplatesPage = lazy(() => import('@/pages/flows/FlowTemplates.Page'));
const BrandSpaceHomePage = lazy(() => import('@/pages/brand-space/home/BrandSpaceHome.Page'));
const BrandSpaceStudioPage = lazy(() => import('@/pages/brand-space/studio/BrandSpaceStudio.Page'));
const ApplicationsPage = lazy(() => import('@/pages/applications/Applications.Page'));
const LegacyApplicationsRedirect = lazy(
  () => import('@/pages/applications/LegacyApplicationsRedirect')
);
const BriefTemplateLibraryPage = lazy(
  () => import('@/pages/collabs/brief-templates/BriefTemplateLibrary.Page')
);
const BriefTemplateEditorPage = lazy(
  () => import('@/pages/collabs/brief-templates/BriefTemplateEditor.Page')
);
const CollabTrackingPage = lazy(() => import('@/pages/collabs/suivi/CollabTracking.Page'));
const UgcTrackingPage = lazy(() => import('@/pages/collabs/ugc/UgcTracking.Page'));
const CoPublicitesPage = lazy(() => import('@/pages/collabs/CoPublicites.Page'));
const GiftTrackingPage = lazy(() => import('@/pages/gifting/GiftTracking.Page'));
const CompanionOnboardingPage = lazy(
  () => import('@/pages/onboarding/companion/CompanionOnboarding.Page')
);
const BrandOnboardingPage = lazy(() => import('@/pages/onboarding/BrandOnboarding.Page'));
import Layout from './components/Layout';
import { CompanionGate } from './components/companion/CompanionGate';
import { UpgradeModal } from './components/subscription';
import { PLANS_PAGE } from './components/subscription/plans-page';
import RouteErrorBoundary from './components/RouteErrorBoundary';
import NavigationHistoryTracker from './libs/navigation-history';
import { withDefaultHydrateFallback } from './libs/router-hydrate-fallback';
import AffiliatesPage from './pages/affiliates/Affiliates.Page';
import AffiliateInvitationsPage from './pages/affiliates/AffiliateInvitations.Page';
import ForgotPasswordPage from './pages/auth/ForgotPassword.page';
import ResetPasswordPage from './pages/auth/ResetPassword.page';
const CreatorDiscoveryPage = lazy(() => import('./pages/creators/discovery/CreatorDiscovery.Page'));
import DashboardPage from './pages/dashboard/Dashboard.Page';
import AuthPage from './pages/auth/AuthPage';
import MentionsPage from './pages/mentions/Mentions.Page';
import MessageThreadPage from './pages/messaging/MessageThread.Page';
import MessageThreadLayout from './pages/messaging/MessageThreadLayout';
import SentBatchDetailPage from './pages/messaging/SentBatchDetail.Page';
import UnselectedMessageTreadPage from './pages/messaging/UnselectedMessageThread.Page';
import ExtensionAuthorizePage from './pages/extension/ExtensionAuthorize';
import ExtensionCallbackPage from './pages/extension/ExtensionCallback';
import NotFoundPage from './pages/NotFoundPage';
import ShopifyOnboardingPage from './pages/onboarding/ShopifyOnboarding.Page';
import { CommissionsPage } from './pages/payments/commissions/CommissionsPage';
import PaymentsLayout from './pages/payments/Payments.Layout';
import PayoutCheckoutLayout from './pages/payments/payouts/checkout/PayoutCheckout.Layout';
import PayoutCheckoutPage from './pages/payments/payouts/checkout/PayoutCheckoutPage';
import PayoutProcessingPage from './pages/payments/payouts/checkout/PayoutConfirmationPage';
import ProgramAffiliatesPage, {
  programAffiliatesLoader,
} from './pages/program-details/affiliates/ProgramAffiliates.Page';
import ProgramInvitationsPage, {
  programInvitationsLoader,
} from './pages/program-details/invitations/ProgramInvitations.Page';
import AffiliateProgramOverview, {
  affiliateProgramLoader,
} from './pages/program-details/ProgramOverview.Page';
import ProgramsBrowsePage from './pages/programs/ProgramsBrowse.Page';
import GeneralSettingsPage, { generalSettingsLoader } from './pages/settings/General.Page';
import IntegrationsPage from './pages/settings/Settings.IntegrationsPage';
import IntegrationDetailPage from './pages/settings/IntegrationDetail.Page';
import SettingsLayout from './pages/settings/Settings.Layout';
import SubscriptionPage from './pages/settings/Subscription.Page';
const InvoicesPage = lazy(() => import('./pages/settings/Invoices.Page'));
import AgentsSettingsPage from './pages/settings/Agents.Page';
import AccountProfileSettingsPage from './pages/settings/account/Profile.Page';
import AccountPreferencesSettingsPage from './pages/settings/account/Preferences.Page';
import AccountNotificationsSettingsPage from './pages/settings/account/Notifications.Page';
import AccountSecuritySettingsPage from './pages/settings/account/Security.Page';
import JoinTeamPage from './pages/team/JoinTeam.Page';
import { LoadingPage } from './components/ui/loading-page';
import ShopifyWelcomePage from './pages/welcome/ShopifyWelcome.Page';

import ProtectedRoute from './ProtectedRoute';
import type { OnboardingStep } from './pages/onboarding/_components/onboarding-steps';
import { ONBOARDING_PATHS } from './pages/onboarding/_components/onboarding-steps';
import McpAuthorizePage from './pages/mcp/McpAuthorize.Page';
import McpCopublicityReviewPage from './pages/mcp/McpCopublicityReview.Page';
import AccountAssistantsLayout from './pages/settings/account/AccountAssistantsLayout';
import AccountAssistantsPage from './pages/settings/account/Assistants.Page';

const TeamPage = lazy(() => import('./pages/settings/Team.Page'));
const TemplatesSettingsPage = lazy(() => import('./pages/settings/Templates.Page'));
export const routeHydrateFallbackElement = <LoadingPage />;

/**
 * The steps of the brand onboarding v2, one protected route each. They sit
 * under `/onboarding`, which the subscription guard and the 402 interceptor
 * both leave alone.
 */
const brandOnboardingRoutes: RouteObject[] = (
  Object.entries(ONBOARDING_PATHS) as [OnboardingStep, string][]
).map(([step, path]) => ({
  path,
  element: (
    <ProtectedRoute>
      <Suspense fallback={<LoadingPage />}>
        <BrandOnboardingPage step={step} />
      </Suspense>
    </ProtectedRoute>
  ),
}));

// Layout for standard protected routes including the main UI Layout.
// The upgrade modal is mounted here, inside the router, because its plan-limit
// variants navigate (plans page, programmes list, Flows).
const MainProtectedLayout = () => (
  <ProtectedRoute>
    <CompanionGate>
      <Layout />
    </CompanionGate>
    <NavigationHistoryTracker />
    <UpgradeModal />
  </ProtectedRoute>
);

// Layout specifically for Payout Checkout - Protected but without main UI Layout
const PayoutCheckoutProtectedRoute = () => (
  <ProtectedRoute>
    <PayoutCheckoutLayout>
      <Outlet />
    </PayoutCheckoutLayout>
  </ProtectedRoute>
);

const contentRoutes: RouteObject[] = [
  {
    // Shared boundary for every standalone route (no own layout group): on a
    // stale chunk after a deploy it triggers the dynamic-import reload, and it
    // recovers render errors. The layout groups below declare their own.
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/',
        element: <AuthPage />,
      },
      {
        path: '/mcp/authorize',
        element: <McpAuthorizePage />,
      },
      { path: '/mcp/copublicity/review/:id', element: <McpCopublicityReviewPage /> },
      {
        element: <AccountAssistantsLayout />,
        children: [{ path: '/settings/account/assistants', element: <AccountAssistantsPage /> }],
      },
      {
        path: '/login',
        element: <Navigate to="/" replace />,
      },
      // Brand self sign-up. Back to the login page while the sign-up is closed.
      {
        path: '/signup',
        element: <AuthPage entry="signup" />,
      },
      // Password reset routes
      {
        path: '/forgot-password',
        element: <ForgotPasswordPage />,
      },
      {
        path: '/reset-password',
        element: <ResetPasswordPage />,
      },
      // Team invitation acceptance (public)
      {
        path: '/team/join',
        element: <JoinTeamPage />,
      },
      // Onboarding routes (protected)
      ...brandOnboardingRoutes,
      {
        path: '/onboarding/shopify',
        element: (
          <ProtectedRoute>
            <ShopifyOnboardingPage />
          </ProtectedRoute>
        ),
      },
      {
        // Companion onboarding wizard: full-screen, outside the main UI Layout.
        path: '/onboarding/companion',
        element: (
          <ProtectedRoute>
            <Suspense fallback={null}>
              <CompanionOnboardingPage />
            </Suspense>
          </ProtectedRoute>
        ),
      },
      {
        path: '/welcome/shopify',
        element: (
          <ProtectedRoute>
            <ShopifyWelcomePage />
          </ProtectedRoute>
        ),
      },
      // New route for not-found page
      {
        path: '/not-found',
        element: <NotFoundPage />,
      },
      { path: '/pricing', element: <Navigate to={PLANS_PAGE} replace /> },
      // Chrome Extension auth flow
      {
        path: '/extension/authorize',
        element: <ExtensionAuthorizePage />,
      },
      {
        path: '/extension/callback',
        element: <ExtensionCallbackPage />,
      },
    ],
  },

  // Routes using the Main Layout (Sidebar + TopNav)
  {
    element: <MainProtectedLayout />, // Use the new layout wrapper
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        path: '/dashboard',
        element: <DashboardPage />,
      },
      {
        path: '/creators',
        element: <Navigate to="/mentions?tab=creators" replace />,
      },
      {
        path: '/creators/discovery',
        element: <CreatorDiscoveryPage />,
      },

      {
        path: '/affiliates',
        element: <AffiliatesPage />,
      },
      {
        path: '/affiliates/invitations',
        element: <AffiliateInvitationsPage />,
      },
      {
        path: '/applications',
        element: <ApplicationsPage />,
      },
      {
        path: '/affiliates/applications',
        element: <LegacyApplicationsRedirect />,
      },
      {
        path: '/programs/browse',
        element: <ProgramsBrowsePage />,
      },
      {
        path: '/mentions',
        element: <MentionsPage />,
      },
      {
        path: '/collabs',
        element: <CoPublicitesPage />,
      },
      {
        path: '/collabs/suivi',
        element: <CollabTrackingPage />,
      },
      {
        path: '/collabs/ugc',
        element: <UgcTrackingPage />,
      },
      {
        path: '/collabs/brief-templates',
        element: <BriefTemplateLibraryPage />,
      },
      {
        path: '/collabs/brief-templates/:templateId',
        element: <BriefTemplateEditorPage />,
      },
      {
        path: '/gifting',
        element: <GiftTrackingPage />,
      },
      {
        path: '/flows',
        element: <FlowListPage />,
      },
      {
        // Registered before the editor: "new" and "templates" are not flow ids.
        path: '/flows/new',
        element: <FlowTemplatesPage />,
      },
      {
        // The gallery's first address; links already shared keep working.
        path: '/flows/templates',
        element: <RedirectKeepingSearch to="/flows/new" />,
      },
      {
        path: '/flows/:flowId',
        element: <FlowEditorPage />,
      },
      {
        path: '/brand-space',
        element: <BrandSpaceHomePage />,
      },
      {
        path: '/brand-space/studio',
        element: <BrandSpaceStudioPage />,
      },
      {
        path: '/messages',
        element: <MessageThreadLayout />,
        children: [
          {
            index: true,
            element: <UnselectedMessageTreadPage />,
          },
          {
            path: 'sent/:batchId',
            element: <SentBatchDetailPage />,
          },
          {
            path: ':threadId',
            element: <MessageThreadPage />,
          },
        ],
      },
      {
        path: '/payments',
        element: <PaymentsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="./commissions" replace />,
          },
          {
            path: 'commissions',
            element: <CommissionsPage />,
          },
        ],
      },
      {
        path: '/settings',
        element: <SettingsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="./general" replace />, // Redirect to general settings as default
          },
          {
            path: 'account',
            element: <Navigate to="./profile" replace />,
          },
          {
            path: 'account/profile',
            element: <AccountProfileSettingsPage />,
          },
          {
            path: 'account/preferences',
            element: <AccountPreferencesSettingsPage />,
          },
          {
            path: 'account/notifications',
            element: <AccountNotificationsSettingsPage />,
          },
          {
            path: 'account/security',
            element: <AccountSecuritySettingsPage />,
          },
          {
            path: 'general',
            element: <GeneralSettingsPage />,
            loader: generalSettingsLoader,
            hydrateFallbackElement: routeHydrateFallbackElement,
          },
          {
            // The standalone Brand DNA page merged into Companion settings;
            // ?setup=true keeps deep links opening the edit modal directly.
            path: 'brand-dna',
            element: <Navigate to="/settings/agents?setup=true" replace />,
          },
          {
            path: 'integrations',
            element: <IntegrationsPage />,
          },
          {
            path: 'integrations/:platform',
            element: <IntegrationDetailPage />,
          },
          {
            path: 'billing',
            element: <SubscriptionPage />,
          },
          {
            path: 'invoices',
            element: (
              <Suspense fallback={<LoadingPage />}>
                <InvoicesPage />
              </Suspense>
            ),
          },
          {
            // Agents platform settings (toggles + budget)
            path: 'agents',
            element: <AgentsSettingsPage />,
          },
          {
            path: 'team',
            element: <TeamPage />,
          },
          {
            path: 'templates',
            element: <TemplatesSettingsPage />,
          },
        ],
      },
      {
        path: '/programs/:id',
        element: <Outlet />,
        children: [
          {
            index: true,
            element: <Navigate to="./overview" replace />, // Redirect to overview while retaining the id
          },
          {
            path: 'overview',
            element: <AffiliateProgramOverview />,
            loader: affiliateProgramLoader,
            hydrateFallbackElement: routeHydrateFallbackElement,
          },
          {
            path: 'affiliates',
            element: <ProgramAffiliatesPage />,
            loader: programAffiliatesLoader,
            hydrateFallbackElement: routeHydrateFallbackElement,
          },
          {
            path: 'invitation',
            element: <ProgramInvitationsPage />,
            loader: programInvitationsLoader,
            hydrateFallbackElement: routeHydrateFallbackElement,
          },
        ],
      },
    ],
  },
  // Payout Checkout Route (Protected, but uses PayoutCheckoutLayout instead of main Layout)
  {
    path: '/payout/:payoutId/checkout', // Match the specific path pattern
    element: <PayoutCheckoutProtectedRoute />, // Use the dedicated protected layout for checkout
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="./summary" replace />,
      },
      {
        path: 'summary',
        element: <PayoutCheckoutPage />,
      },
      {
        path: 'processing',
        element: <PayoutProcessingPage />,
      },
    ],
  },
  // Catch-all route for any other paths not matched
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];

export const routes = withDefaultHydrateFallback([
  { element: <ProductAnalyticsRouteTracker />, children: contentRoutes },
]);

const router = createBrowserRouter(routes);

/** A moved page keeps its query: a shared link to a previewed template still opens it. */
function RedirectKeepingSearch({ to }: { to: string }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

export function Router() {
  return (
    <NuqsAdapter>
      <RouterProvider router={router} />
    </NuqsAdapter>
  );
}
